import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  FamilyRestroom,
  Key,
  Search,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AuthState } from "store/features/auth";
import { RootState } from "store";
import { isNurseryAdmin } from "utils/roles";
import { getParents } from "apis";
import ResetPasswordDialog from "components/ResetPasswordDialog";
import DeleteDialog from "components/DeleteUserDialog";
import { stringAvatar } from "utils";
import { toAssetUrl } from "utils/apiConfig";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";

type Parent = {
  id: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  password: string;
  mobile: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
  };
  photo?: string | File;
  settignId: number;
  role: string;
  nurseryId: number;
};

type SortKey = "name" | "email";
type SortDir = "asc" | "desc";

const Parents: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const canMutate = isNurseryAdmin(role);

  const [parents, setParents] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const navigate = useNavigate();
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Parent>();

  const fetchData = useCallback(() => {
    setLoading(true);
    getParents()
      .then((data) =>
        setParents(
          (data || []).map((p: any) => ({
            ...p,
            name: [p.firstName, p.lastName].filter(Boolean).join(" "),
          })),
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDialogClose = useCallback(() => {
    setOpenResetDialog(false);
    setOpenDeleteDialog(false);
    fetchData();
  }, [fetchData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return parents;
    const term = search.toLowerCase();
    return parents.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term) ||
        (p.mobile && p.mobile.includes(term)),
    );
  }, [parents, search]);

  const sorted = useMemo(() => {
    const list = filtered.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "name") return dir * a.name.localeCompare(b.name);
      if (sortKey === "email") return dir * a.email.localeCompare(b.email);
      return 0;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const formatAddress = (addr: Parent["address"]) => {
    if (!addr) return "—";
    const parts = [
      addr.line1,
      addr.line2,
      addr.city,
      addr.postcode,
      addr.country,
    ].filter(Boolean);
    return parts.join(", ") || "—";
  };

  return (
    <>
      {/* Header */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
          flexWrap: "wrap",
          py: 1.5,
        }}
      >
        <FamilyRestroom sx={{ color: "primary.main", fontSize: 24 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("parents.label")}
        </Typography>
        <Chip
          label={parents.length}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            fontWeight: 600,
          }}
        />
        <Box
          sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
        >
          <TextField
            size="small"
            placeholder={t("placeholders.select") + "..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 14 },
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
        </Box>
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Container sx={{ py: 2 }}>
          {sorted.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={8}
              color="text.secondary"
            >
              <FamilyRestroom
                sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
              />
              <Typography variant="h6" gutterBottom>
                {search ? t("attendance.no_match") : t("parents.empty")}
              </Typography>
              {!search && canMutate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate("/parents/add")}
                  sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
                >
                  {t("parents.add")}
                </Button>
              )}
            </Box>
          ) : (
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden" }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "name"}
                          direction={sortKey === "name" ? sortDir : "asc"}
                          onClick={() => handleSort("name")}
                        >
                          {t("inputs.full_name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "email"}
                          direction={sortKey === "email" ? sortDir : "asc"}
                          onClick={() => handleSort("email")}
                        >
                          {t("inputs.email")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("inputs.mobile")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("labels.address")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("labels.action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((parent) => (
                        <TableRow key={parent.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar
                                src={toAssetUrl(
                                  parent.photo as string | undefined,
                                )}
                                {...(!parent.photo
                                  ? stringAvatar(parent.name, {
                                      width: 32,
                                      height: 32,
                                      fontSize: 13,
                                    })
                                  : {
                                      sx: {
                                        width: 32,
                                        height: 32,
                                        fontSize: 13,
                                      },
                                    })}
                              />
                              <Typography variant="body2" fontWeight={500}>
                                {parent.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {parent.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {parent.mobile || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={formatAddress(parent.address)}
                            >
                              {formatAddress(parent.address)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {canMutate ? (
                              <>
                                <Tooltip title={t("labels.edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigate("/parents/edit", {
                                        state: parent,
                                      })
                                    }
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("labels.reset_password")}>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => {
                                      setSelectedItem(parent);
                                      setOpenResetDialog(true);
                                    }}
                                  >
                                    <Key fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("buttons.delete")}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setSelectedItem(parent);
                                      setOpenDeleteDialog(true);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
              <TablePaginationLocalizationProvider>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={sorted.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TablePaginationLocalizationProvider>
            </Card>
          )}
        </Container>
      )}

      {/* Add FAB */}
      {canMutate && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            pl: 2.5,
            pr: 3,
            gap: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: "0 4px 14px 0 rgba(0,0,0,0.25)",
            "&:hover": { boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)" },
          }}
          onClick={() => navigate("/parents/add")}
        >
          <Add />
          {t("parents.add")}
        </Fab>
      )}

      {/* Dialogs */}
      {openResetDialog && selectedItem && (
        <ResetPasswordDialog
          open={openResetDialog}
          onClose={handleDialogClose}
          item={selectedItem}
        />
      )}
      {openDeleteDialog && selectedItem && (
        <DeleteDialog
          open={openDeleteDialog}
          onClose={handleDialogClose}
          item={selectedItem}
        />
      )}
    </>
  );
};

export default Parents;
